/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextFieldSts } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import {
  SubmitVoucherCode,
  useSubmitCodes,
} from "@/hooks/api/review-api/apply-promo";
import useGtmApplyDiscount from "@/hooks/gtm/quote-review/use-gtm-quote-review-discount-options";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { AddPromoCodeTypes } from "@/types/review-types";
import { quoteReviewDiscountDataMap } from "@/utils/gtm/quote-review-data-map";
import {
  addVoucherCode,
  promoReturnMessage,
  removeAllSpaces,
  voucherAmount,
  voucherReturnMessage,
} from "@/utils/review.utils";

import styles from "./add-promo-code.module.css";

function AddPromoCode(props: Readonly<AddPromoCodeTypes>) {
  const {
    title,
    subTitle,
    promoCodeInputPlaceholder,
    promoCodeButtonLabel,
    voucherCodePlaceholder,
    voucherCodeButtonLabel,
    promoErrorCodeAlreadyApplied,
    promoSuccessMassage,
    promoErrorCodeLimitReached,
    promoErrorCodeInvalid,
    voucherErrorAlreadyTwoCodeApplied,
    voucherErrorCodeApplied,
    voucherErrorInvalidCode,
    voucherSuccessMassage,
    voucherErrorCodeLimitReached,
  } = props;
  const { version, type, attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const dispatch = useDispatch();
  const isQuoteDisabled = useQuoteDisabled();
  const { current } = useWizard();
  const promoCodeTrimEnabled = Boolean(
    current?.brandSpecificParams?.promoCodeTrimEnabled
  );
  const [promoCode, setPromoCode] = useState<string>("");
  const [promoCodeUpdated, setPromoCodeUpdated] = useState<string>("");
  const [promoButtonDisabled, setPromoButtonDisabled] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [voucherButtonDisabled, setVoucherButtonDisabled] =
    useState<boolean>(false);

  const [voucherCode, setVoucherCode] = useState<SubmitVoucherCode[]>([]);
  const [newVoucherCode, setNewVoucherCode] = useState<
    SubmitVoucherCode | undefined
  >();
  const [newQuote, submitCodes]: any = useSubmitCodes(
    { version, type, attributes, referenceId },
    promoCode,
    [],
    setPromoButtonDisabled,
    setIsLoading,
    promoCodeTrimEnabled
  );
  const [showValidationPromo, setShowValidationPromo] = useState(false);
  const [showValidationVoucher, setShowValidationVoucher] = useState(false);
  const [isGtmUpdated, setIsGtmUpdated] = useState(false);
  const [voucherCodeGTM, setVoucherCodeGTM] = useState<string>("");

  const [newQuoteVoucher, submitCodesVoucher]: any = useSubmitCodes(
    { version, type, attributes, referenceId },
    promoCodeUpdated,
    voucherCode,
    setVoucherButtonDisabled
  );

  const validVouchers = useMemo(() => {
    return voucherAmount(attributes);
  }, [attributes]);

  const hasPartnerCode = !!(
    attributes?.paymentDetails?.discounts?.promoCode?.valid &&
    attributes?.partnerCode
  );

  // GTM trigger for discount

  const resetTrigger = useCallback(() => {
    setIsGtmUpdated(false);
  }, []);

  let promoAndVoucher = null;
  if (attributes) {
    promoAndVoucher = quoteReviewDiscountDataMap(
      attributes,
      voucherCodeGTM,
      isGtmUpdated
    );
  }

  useGtmApplyDiscount({
    applyVoucherData: promoAndVoucher,
    isReady: isGtmUpdated,
    resetGtmTiger: resetTrigger,
  });

  useEffect(() => {
    if (attributes?.paymentDetails?.discounts?.promoCode) {
      setPromoCodeUpdated(
        attributes?.paymentDetails?.discounts?.promoCode?.code
      );
    } else {
      setPromoCodeUpdated("");
    }
    if (
      attributes?.paymentDetails?.discounts?.promoCode?.valid &&
      attributes?.partnerCode
    ) {
      setShowValidationPromo(true);
    }
  }, [attributes]);

  useEffect(() => {
    if (newQuote) {
      const updatedQuote = {
        ...newQuote,
        attributes: {
          ...(newQuote?.attributes ?? attributes),
          paymentDetails: {
            ...newQuote.attributes.paymentDetails,
            optOutAutoRenewal: attributes?.paymentDetails?.optOutAutoRenewal,
          },
        },
      };
      dispatch(updateQuote(updatedQuote));
      setPromoCode("");
      setShowValidationPromo(true);
    }
  }, [dispatch, newQuote]);

  useEffect(() => {
    if (newQuoteVoucher) {
      const updatedQuoteVoucher = {
        ...newQuoteVoucher,
        attributes: {
          ...(newQuoteVoucher?.attributes ?? attributes),
          paymentDetails: {
            ...newQuoteVoucher.attributes.paymentDetails,
            optOutAutoRenewal: attributes?.paymentDetails?.optOutAutoRenewal,
          },
        },
      };
      dispatch(updateQuote(updatedQuoteVoucher));
      setNewVoucherCode(undefined);
      setShowValidationVoucher(true);
      setIsGtmUpdated(true);
    }
  }, [dispatch, newQuoteVoucher]);

  useEffect(() => {
    if (voucherCode.length && newVoucherCode) submitCodesVoucher();
  }, [voucherCode]);

  const promoMessage = useCallback((): {
    message: string;
    state: string;
  } => {
    return promoReturnMessage(
      attributes,
      showValidationPromo,
      promoCode,
      promoErrorCodeAlreadyApplied,
      promoSuccessMassage,
      promoErrorCodeLimitReached,
      promoErrorCodeInvalid
    );
  }, [promoCode, attributes, showValidationPromo]);

  const voucherMessage = useCallback((): {
    message: string;
    state: string;
  } => {
    return voucherReturnMessage(
      attributes,
      newVoucherCode,
      validVouchers,
      showValidationVoucher,
      voucherErrorAlreadyTwoCodeApplied,
      voucherErrorCodeApplied,
      voucherErrorInvalidCode,
      voucherSuccessMassage,
      voucherErrorCodeLimitReached
    );
  }, [newVoucherCode, attributes]);

  function addVoucher(
    newCode: SubmitVoucherCode | undefined
  ): SubmitVoucherCode[] {
    return addVoucherCode(newCode, attributes, validVouchers, voucherCode);
  }

  return (
    <div data-testid="AddPromoCode-testid">
      <div>
        <h1 className=" font-header-font full text-[23px] md:text-[25px] leading-[26px] md:leading-[28px] text-text-title mb-spacing-xs">
          {title}
        </h1>
        {subTitle && (
          <div className=" leading-[24px] text-[16px] font-normal font-body-font ">
            {subTitle}
          </div>
        )}
      </div>

      <div className="max-w-[414px]">
        <div className="flex gap-spacing-s w-full mt-spacing-l">
          <TextFieldSts
            isDisabled={
              (hasPartnerCode ?? promoButtonDisabled) || isQuoteDisabled
            }
            label={promoCodeInputPlaceholder}
            name="promoCode"
            id="promoCode"
            width="calc(100% - 94px)"
            message={promoMessage().message ?? ""}
            state={promoMessage().state ?? ""}
            value={promoCode}
            maxLength={20}
            labelLeftPadding="16px"
            onChange={(e) => {
              const processedCode = promoCodeTrimEnabled
                ? removeAllSpaces(e)
                : e;
              setPromoCode(processedCode);
              if (processedCode) {
                setShowValidationPromo(true);
              } else {
                setShowValidationPromo(false);
              }
            }}
          />
          <LabelButtonWrapper
            data-testid="add-promo-button"
            id="add-promo-button"
            isDisabled={
              promoButtonDisabled ||
              isQuoteDisabled ||
              !promoCode ||
              !!(
                attributes?.paymentDetails?.discounts?.promoCode?.valid &&
                promoCode
              )
            }
            label={promoCodeButtonLabel}
            hierarchy="secondary"
            type="button"
            onClick={() => {
              submitCodes();
            }}
            className={`!w-[82px] h-[56px] ${
              hasPartnerCode
                ? `disabled:text-text-disabled disabled:bg-transparent disabled:outline-none disabled:border-border-disabled !disabled:hover:border-border-disabled disabled:hover:text-text-disabled ${styles.disabledPromoBtn}`
                : ""
            }`}
            isLoading={isLoading}
            loadingIcon={<BtnLoadingSvg />}
          />
        </div>

        <div className="flex gap-spacing-s w-full mt-spacing-l">
          <TextFieldSts
            isDisabled={voucherButtonDisabled || isQuoteDisabled}
            label={voucherCodePlaceholder}
            name="voucherCode"
            id="voucherCode"
            width="calc(100% - 94px)"
            message={voucherMessage().message ?? ""}
            state={voucherMessage().state ?? ""}
            value={newVoucherCode?.code}
            maxLength={20}
            labelLeftPadding="16px"
            onChange={(e) => {
              setNewVoucherCode({
                index: validVouchers?.length
                  ? Number(validVouchers?.length) + 1
                  : 1,
                code: e,
              });
              setVoucherCodeGTM(e);

              if (e) {
                setShowValidationVoucher(true);
              } else {
                setShowValidationVoucher(false);
              }
            }}
          />
          <LabelButtonWrapper
            data-testid="add-voucher-button"
            id="add-voucher-button"
            isDisabled={
              voucherButtonDisabled ||
              isQuoteDisabled ||
              !newVoucherCode?.code ||
              (validVouchers && validVouchers?.length >= 2) ||
              attributes?.paymentDetails?.discounts?.voucher?.some(
                (item) => item.code === newVoucherCode?.code && !item.message
              )
            }
            label={voucherCodeButtonLabel}
            hierarchy="secondary"
            type="button"
            onClick={() => {
              setVoucherCode(addVoucher(newVoucherCode));
            }}
            className="!w-[82px] h-[56px]"
            isLoading={voucherButtonDisabled}
            loadingIcon={<BtnLoadingSvg />}
          />
        </div>
      </div>
    </div>
  );
}

export default AddPromoCode;
